import { User as UserIcon } from 'lucide-react';
import { useSelector } from 'react-redux';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useUserAvatar } from '@/hooks/useUserAvatar';
import { cn } from '@/lib/utils';
import { RootState } from '@/redux/store';
import { getAvatarInitials, resolveAvatarUrl } from '@/utils/avatar';

type UserAvatarProps = {
  username?: string | null;
  src?: string | null;
  name?: string | null;
  size?: '48' | '128' | '256';
  className?: string;
  fallbackClassName?: string;
  alt?: string;
  skipAvatarFetch?: boolean;
};

export const UserAvatar = ({
  username,
  src,
  name,
  size = '256',
  className,
  fallbackClassName,
  alt,
  skipAvatarFetch = false,
}: UserAvatarProps) => {
  const resolvedListSrc = src?.trim() ? resolveAvatarUrl(src) : null;
  const shouldFetchAvatarMetadata = Boolean(username) && !skipAvatarFetch;

  // Hooks must run unconditionally; the piiMode branch comes after.
  const { avatarUrl } = useUserAvatar({
    username,
    enabled: shouldFetchAvatarMetadata,
    size,
  });
  const piiMode = useSelector((s: RootState) => s.privacy?.piiMode ?? false);

  // Shoulder-surf protection: drop both the image and the initials,
  // render a generic silhouette instead.
  if (piiMode) {
    return (
      <Avatar className={className}>
        <AvatarFallback
          className={cn('bg-gray-200 text-gray-500', fallbackClassName)}
          aria-label="PII masked"
        >
          <UserIcon className="h-1/2 w-1/2" />
        </AvatarFallback>
      </Avatar>
    );
  }

  const imageSrc = avatarUrl ?? resolvedListSrc ?? undefined;
  const fallbackLabel = getAvatarInitials(name);

  return (
    <Avatar className={className}>
      <AvatarImage src={imageSrc} alt={alt ?? name ?? 'User avatar'} />
      <AvatarFallback className={cn(fallbackClassName)}>{fallbackLabel}</AvatarFallback>
    </Avatar>
  );
};
